import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { Button, Box, Typography, Tooltip, Slider, Stack } from '@mui/material';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { editorAPI } from 'api/editor';
import IconMi from 'components/common/icon/IconMi';
import UploadFile from 'components/common/upload-control/UploadFile';

import styles from './PublicImagePreview.styles';
import useBundleTranslation from 'i18n';
import { getElementFieldValue, processSettingsUrl } from 'components/common/form/formTools';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { instance } from 'api/api';
import useHasImageToRemove from 'components/element-image/useHasImageToRemove';
import { FormComponentBuilder } from 'components/common/form/layout';
import { Params as UrlParams } from 'react-router';

export interface FormControlPublicImagePreviewProps extends FormControlProps {
    urlParams: UrlParams;
    name: string;
    previewUrl: string;
    saveImgUrl: string;
    uploadImageUrl: string;
    thumbnailUrl: string;
    hasImageUrl: string;
    deleteImageUrl: string;
    blurSliderName: string;
}

const fileFieldName = 'homepage_image';
const timestamp = Date.now();

const PublicImagePreview = ({
    controlProps,
    elementProps,
}: FormElementControlPropsType<FormControlPublicImagePreviewProps>) => {
    const { t } = useBundleTranslation(['components/common/element_image', 'components/branding']);
    const params = useParams();
    const [imageKey, setImageKey] = useState(timestamp);
    const formValues = controlProps.form.hookFormGetValues();
    const [isLoading, toggleLoading] = useState(false);
    const [previewFiles, setPreviewFiles] = useState<FileList | null>(null);
    const [uploadImageFormActive, setUploadImageFormActive] = useState<boolean>(false);
    const [isImageLoading, toggleImageLoading] = useState(false);

    const sliderValue = controlProps.form.hookFormWatch(controlProps.blurSliderName);

    const [mainImageStamp, setMainImageStamp] = useState('');
    useEffect(() => {
        const interval = setInterval(() => {
            const parentStamp = getElementFieldValue(controlProps.form, 'image_control.stamp');
            if (parentStamp != null) {
                setMainImageStamp(parentStamp);
            }
        }, 1000);
        return () => {
            window.clearInterval(interval);
        };
    }, []);

    const urlParams = { ...controlProps.urlParams, stamp: String(imageKey) + '_' + String(mainImageStamp) };
    const pks = Object.keys(urlParams);

    const hasImageUrl = processSettingsUrl(controlProps.hasImageUrl, pks, urlParams);
    const deleteImageUrl = processSettingsUrl(controlProps.deleteImageUrl, pks, urlParams);

    const {
        hasImageToRemove,
        setHasImageToRemove,
        refetch: refetchHasImage,
    } = useHasImageToRemove(controlProps.name, hasImageUrl);

    const queryBlurValue = !hasImageToRemove ? sliderValue : '0';

    const previewUrl = processSettingsUrl(controlProps.previewUrl, [...pks, 'blur'], {
        ...urlParams,
        blur: queryBlurValue,
    });
    const thumbnailUrl = processSettingsUrl(controlProps.thumbnailUrl, [...pks, 'blur'], {
        ...urlParams,
        blur: queryBlurValue,
    });
    const uploadImageUrl = processSettingsUrl(controlProps.uploadImageUrl, [...pks, 'segmentId'], {
        ...urlParams,
        segmentId: '0',
    });

    const uploadPopupSettings: PopupSettings = {
        title: t('upload_image'),
    };

    const operateFormCustomImage = async (imageValue: 'Y' | 'N') => {
        elementProps?.form.hookFormSetValue('use_custom_no_access_image_ind', imageValue ?? '');
        await elementProps?.form.formSave();
        await controlProps.form.formRefetch();
    };

    const saveImagePreview = async () => {
        if (params.elementId && previewFiles?.[0]) {
            try {
                toggleLoading(true);

                const isOk = await editorAPI.uploadEditorImagePreview(uploadImageUrl, previewFiles[0]);
                setImageKey(Date.now());
                toggleImageLoading(true);
                refetchHasImage();

                if (isOk) {
                    await operateFormCustomImage('Y');
                    setUploadImageFormActive(false);
                }
            } finally {
                toggleLoading(false);
            }
        }
    };

    const changeSlideBlurValue = (event: any, nextValue: number | number[]) => {
        controlProps.form.hookFormSetValue(controlProps.blurSliderName, nextValue, { shouldDirty: true });
        setImageKey(Date.now());
        toggleImageLoading(true);
    };

    const onImageLoaded = () => {
        toggleImageLoading(false);
    };

    const [isDeletingImage, setIsDeletingImage] = useState(false);
    const handleRestoreImageClick = () => {
        setIsDeletingImage(true);
        instance
            .post(deleteImageUrl)
            .then((response) => {
                // Update stamp to refresh Image
                setImageKey(Date.now());
                setHasImageToRemove(false);
                refetchHasImage();
            })
            .finally(() => setIsDeletingImage(false));
    };

    return (
        <Box>
            {controlProps.previewUrl && (
                <Box sx={styles.preview}>
                    {(isImageLoading || isDeletingImage) && (
                        <LoadingPlaceholder variant={'secondary'} relativeToParent />
                    )}
                    <Box>
                        <Typography variant="body1" gutterBottom component="div">
                            {t('homepage_preview_image')}
                        </Typography>

                        <Box sx={{ ...styles.previewImage, width: '552px', minHeight: '368px' }}>
                            <Tooltip followCursor title={`${t('public_image')}`}>
                                <img
                                    key={`preview_image_${imageKey}`}
                                    src={`${process.env.REACT_APP_BACKEND_API_BASE_URL}${previewUrl}`}
                                />
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box sx={styles.thumbnail}>
                        <Typography variant="body1" gutterBottom component="div">
                            {t('homepage_thumbnail')}
                        </Typography>

                        <Box sx={{ ...styles.previewImage, width: '137px' }}>
                            <Tooltip followCursor title={`${t('public__thumbnail_image')}`}>
                                <img
                                    key={`thumbnail_image_${imageKey}`}
                                    src={`${process.env.REACT_APP_BACKEND_API_BASE_URL}${thumbnailUrl}`}
                                    onLoad={onImageLoaded}
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            )}

            {!hasImageToRemove && (
                <>
                    <Typography gutterBottom>{t('preview_image_blur')}</Typography>
                    <Box sx={styles.sliderBox}>
                        <Slider min={0} max={10} onChange={changeSlideBlurValue} value={Number(sliderValue)} />
                        <Typography sx={styles.sliderValue}>{sliderValue}</Typography>
                    </Box>
                </>
            )}

            <Stack spacing={2} direction="row" sx={styles.actions}>
                <Box>
                    <Button
                        data-test={controlProps.name + '-upload-preview-button'}
                        variant="light"
                        startIcon={<IconMi icon="upload" />}
                        onClick={() => setUploadImageFormActive(true)}
                    >
                        {t('upload_preview_image')}
                    </Button>
                </Box>
                {hasImageToRemove && (
                    <Box>
                        <Button
                            data-test={controlProps.name + '-restore-default-image-button'}
                            onClick={handleRestoreImageClick}
                            variant="light"
                            startIcon={<IconMi icon="refresh-cw" />}
                        >
                            {t('restore_default_image')}
                        </Button>
                    </Box>
                )}
            </Stack>

            <Popup
                maxWidth="popupMd"
                isLoading={isLoading}
                settings={uploadPopupSettings}
                open={uploadImageFormActive}
                onConfirm={saveImagePreview}
                okDisabled={!previewFiles}
                onHide={() => setUploadImageFormActive(false)}
            >
                <Box>
                    <Typography variant="body1" gutterBottom component="div">
                        {t('upload_image_popup_label')}
                    </Typography>
                    <UploadFile
                        controlProps={{
                            ...controlProps,
                            view: 'field',
                            name: fileFieldName,
                            buttonText: t('upload_logo_field_button_text', { ns: 'components/branding' }),
                            inputPlaceholder: t('upload_logo_field_placeholder', { ns: 'components/branding' }),
                            accept: 'image/png',
                            onFilesChange: setPreviewFiles,
                        }}
                    />
                </Box>
                <Box sx={styles.restriction}>
                    <Typography variant="body1" gutterBottom component="div">
                        <Trans t={t} i18nKey="upload_image_restriction_2" components={{ br: <br /> }} />
                    </Typography>
                </Box>
            </Popup>
        </Box>
    );
};

export default PublicImagePreview;

export class PublicImagePreviewBuilder extends FormComponentBuilder {
    prepareProps(): FormControlPublicImagePreviewProps {
        return {
            ...this.controlProps,
            urlParams: {
                ...this.elementProps.urlParams,
                ...this.elementProps.componentProps?.urlParams,
            } as UrlParams,
            previewUrl: this.elementProps.componentProps?.previewUrl ?? '',
            uploadImageUrl: this.elementProps.componentProps?.uploadImageUrl ?? '',
            saveImgUrl: this.elementProps.componentProps?.setupImgUrl ?? '',
            thumbnailUrl: this.elementProps.componentProps?.thumbnailUrl ?? '',
            hasImageUrl: this.elementProps.componentProps?.hasImageUrl ?? '',
            deleteImageUrl: this.elementProps.componentProps?.deleteImageUrl ?? '',
            blurSliderName: this.elementProps.componentProps?.blurSliderName ?? '',
        };
    }
}
